import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, LocateFixed } from 'lucide-react';
import { loadGoogleMaps } from '../../utils/googleMaps';
import { useTheme } from '../../contexts/ThemeContext';
import { salesAPI } from '../../services/salesApi';
import { Column, DataTable, FilterSelect, GHOST_BTN, ListToolbar, Notice, Stat, Stats, StatusChip } from '../shared/listPage';
import {
  ACTIVE_STATUSES, KIND_OPTIONS, Lead, STATUS_COLORS, STATUS_OPTIONS, geocodeQuery, kindLabel, relativeDay, statusLabel,
  statusTone,
} from './salesModel';

/**
 * Πωλήσεις → «Χάρτης» (migration 286, απόφαση χρήστη 3/11/2026): οι υποψήφιοι πάνω στον χάρτη, χρώμα ανά στάδιο, και
 * δίπλα όσοι φαίνονται στο τρέχον κομμάτι του χάρτη — για να μαζεύονται οι επισκέψεις ανά περιοχή.
 *
 * Σημεία: όσοι δεν έχουν, βρίσκονται ΜΙΑ φορά εδώ (Google Geocoder, όπως το delivery) και γράφονται στη βάση
 * (PUT /leads/:id/geo)· «δεν βρέθηκε» γράφεται κι αυτό ('none'), για να μην ξαναψάχνεται. Χωρίς διεύθυνση, το σημείο
 * είναι το κέντρο της περιοχής (κατά προσέγγιση — φαίνεται πιο αχνό).
 */

const ATHENS = { lat: 37.9838, lng: 23.7275 };
const HIDE_POI_LABELS: google.maps.MapTypeStyle = { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] };
// Ίδια με τον χάρτη των ζωνών delivery (DeliveryZoneEditor).
const MAP_STYLES: Record<'light' | 'dark', google.maps.MapTypeStyle[]> = {
  light: [HIDE_POI_LABELS],
  dark: [
    { elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#1e293b' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#94a3b8' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#334155' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#64748b' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f172a' }] },
    HIDE_POI_LABELS,
  ],
};
const GEOCODE_GAP_MS = 250;

interface Props {
  leads: Lead[];
  today: string;
  onOpen: (id: number) => void;
  /** Νέο σημείο στη βάση → ενημέρωση της λίστας του γονέα (χωρίς να ξαναφορτώσει). */
  onGeo: (id: number, geo: Pick<Lead, 'lat' | 'lng' | 'geo_precision'>) => void;
}

const hasPoint = (l: Lead) => typeof l.lat === 'number' && typeof l.lng === 'number';

const SalesMap: React.FC<Props> = ({ leads, today, onOpen, onGeo }) => {
  const { resolved: mode } = useTheme();
  const mapEl = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Map<number, google.maps.Marker>>(new Map());
  const infoRef = useRef<google.maps.InfoWindow | null>(null);
  // Ο χάρτης χωρά όλα τα σημεία όσο προστίθενται (η γεωκωδικοποίηση τα φέρνει ένα-ένα) — ώσπου να τον μετακινήσει
  // ο χρήστης. «Δείξε όλους» ξαναχωρά και ξαναρχίζει να ακολουθεί.
  const fittedCountRef = useRef(0);
  const userMovedRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [kindFilter, setKindFilter] = useState('');
  const [bounds, setBounds] = useState<google.maps.LatLngBounds | null>(null);
  const [geocoding, setGeocoding] = useState<{ done: number; total: number } | null>(null);

  const shown = useMemo(() => leads.filter(l => {
    if (statusFilter === 'active' && !ACTIVE_STATUSES.includes(l.status)) return false;
    if (statusFilter && statusFilter !== 'active' && statusFilter !== 'all' && l.status !== statusFilter) return false;
    return !kindFilter || l.kind === kindFilter;
  }), [leads, statusFilter, kindFilter]);
  const onMap = useMemo(() => shown.filter(hasPoint), [shown]);
  const approx = onMap.filter(l => l.geo_precision === 'area').length;
  const missing = shown.filter(l => !hasPoint(l)).length;
  const visible = useMemo(() => (bounds ? onMap.filter(l => bounds.contains({ lat: l.lat as number, lng: l.lng as number })) : onMap),
    [onMap, bounds]);

  // ── Χάρτης ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(() => {
        if (cancelled || !mapEl.current || mapRef.current) return;
        const map = new google.maps.Map(mapEl.current, {
          center: ATHENS, zoom: 11, mapTypeControl: false, streetViewControl: false, fullscreenControl: true,
          styles: MAP_STYLES[mode],
        });
        map.addListener('idle', () => setBounds(map.getBounds() || null));
        map.addListener('dragstart', () => { userMovedRef.current = true; });
        mapRef.current = map;
        infoRef.current = new google.maps.InfoWindow();
        setReady(true);
      })
      .catch(() => { if (!cancelled) setMapError('Ο χάρτης δεν φόρτωσε (κλειδί Google Maps ή σύνδεση).'); });
    return () => { cancelled = true; };
    // Μία φορά· το θέμα αλλάζει με setOptions πιο κάτω.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (ready) mapRef.current?.setOptions({ styles: MAP_STYLES[mode] }); }, [ready, mode]);

  // Το παράθυρο του σημείου δείχνει πάντα τα τρέχοντα στοιχεία (στάδιο κ.λπ.), όχι όσα ίσχυαν όταν μπήκε το σημείο.
  const byIdRef = useRef<Map<number, Lead>>(new Map());
  byIdRef.current = new Map(leads.map(l => [l.id, l]));

  // Σημεία: ένα ανά υποψήφιο που φαίνεται, ενημέρωση στη θέση (όχι σβήσιμο/ξαναστήσιμο όλων).
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    const keep = new Set(onMap.map(l => l.id));
    markersRef.current.forEach((m, id) => { if (!keep.has(id)) { m.setMap(null); markersRef.current.delete(id); } });
    for (const l of onMap) {
      const icon: google.maps.Symbol = {
        path: google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: STATUS_COLORS[l.status],
        fillOpacity: l.geo_precision === 'area' ? 0.45 : 1, strokeColor: '#ffffff', strokeWeight: 2,
      };
      const position = { lat: l.lat as number, lng: l.lng as number };
      let m = markersRef.current.get(l.id);
      if (!m) {
        m = new google.maps.Marker({ map, position, icon, title: l.name });
        const id = l.id;
        m.addListener('click', () => {
          const cur = byIdRef.current.get(id);
          if (!cur) return;
          const box = document.createElement('div');
          box.style.cssText = 'font-family:inherit;font-size:13px;line-height:1.4;max-width:240px;color:#111';
          const title = document.createElement('div');
          title.style.cssText = 'font-weight:600;margin-bottom:2px';
          title.textContent = cur.name;
          const meta = document.createElement('div');
          meta.style.color = '#555';
          meta.textContent = [statusLabel(cur.status), kindLabel(cur.kind), cur.area].filter(Boolean).join(' · ')
            + (cur.geo_precision === 'area' ? ' · θέση κατά προσέγγιση' : '');
          const open = document.createElement('button');
          open.type = 'button';
          open.textContent = 'Άνοιγμα';
          open.style.cssText = 'margin-top:6px;padding:4px 10px;border-radius:8px;border:1px solid #2563EB;color:#2563EB;background:#fff;cursor:pointer';
          open.addEventListener('click', () => { infoRef.current?.close(); onOpen(id); });
          box.append(title, meta, open);
          infoRef.current?.setContent(box);
          infoRef.current?.open({ map, anchor: m });
        });
        markersRef.current.set(l.id, m);
      } else {
        m.setPosition(position);
        m.setIcon(icon);
      }
    }
    if (!userMovedRef.current && onMap.length !== fittedCountRef.current) fitAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, onMap, onOpen]);

  function fitAll() {
    const map = mapRef.current;
    if (!map || !onMap.length) return;
    const b = new google.maps.LatLngBounds();
    onMap.forEach(l => b.extend({ lat: l.lat as number, lng: l.lng as number }));
    map.fitBounds(b, 48);
    // Ένα-δύο κοντινά σημεία δεν πρέπει να φέρνουν τον χάρτη σε επίπεδο κτιρίου.
    google.maps.event.addListenerOnce(map, 'idle', () => { if ((map.getZoom() || 0) > 14) map.setZoom(14); });
    fittedCountRef.current = onMap.length;
  }
  const showAll = () => { userMovedRef.current = false; fitAll(); };

  // Γεωκωδικοποίηση όσων δεν έχουν σημείο — ένας-ένας, μία φορά ανά υποψήφιο.
  const triedRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (!ready) return undefined;
    const queue = leads.filter(l => !hasPoint(l) && l.geo_precision !== 'none' && !triedRef.current.has(l.id) && geocodeQuery(l));
    if (!queue.length) return undefined;
    let stop = false;
    const geocoder = new google.maps.Geocoder();
    setGeocoding({ done: 0, total: queue.length });
    (async () => {
      for (let i = 0; i < queue.length && !stop; i += 1) {
        const l = queue[i];
        triedRef.current.add(l.id);
        const q = geocodeQuery(l)!;
        try {
          const { results } = await geocoder.geocode({ address: q.query, region: 'GR' });
          const loc = results[0]?.geometry?.location;
          if (loc) {
            const r = await salesAPI.setGeo(l.id, { lat: loc.lat(), lng: loc.lng(), precision: q.precision });
            onGeo(l.id, { lat: r.geo.lat, lng: r.geo.lng, geo_precision: r.geo.geo_precision as Lead['geo_precision'] });
          }
        } catch (e: any) {
          // Δεν βρέθηκε (ZERO_RESULTS) ή σημείο εκτός Ελλάδας → «none», να μην ξαναψάχνεται. Όριο/δίκτυο → ξανά αργότερα.
          const code = e?.code || e?.message || '';
          if (/ZERO_RESULTS/.test(code) || e?.response?.status === 400) {
            await salesAPI.setGeo(l.id, { precision: 'none' }).catch(() => {});
            onGeo(l.id, { lat: null, lng: null, geo_precision: 'none' });
          }
        }
        if (!stop) setGeocoding({ done: i + 1, total: queue.length });
        await new Promise(r => setTimeout(r, GEOCODE_GAP_MS));
      }
      if (!stop) setGeocoding(null);
    })();
    return () => { stop = true; };
    // Τρέχει όταν φορτώσει ο χάρτης ή έρθουν νέοι υποψήφιοι· οι ήδη δοκιμασμένοι κρατιούνται στο triedRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, leads.length]);

  const columns = useMemo<Column<Lead>[]>(() => [
    {
      key: 'name', header: 'Επωνυμία', width: 'minmax(160px, 2fr)', stack: 'title', sortable: true,
      render: l => (
        <>
          <span className="flex items-center gap-2 font-medium text-t1">
            <span aria-hidden="true" className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ background: STATUS_COLORS[l.status] }} />
            {l.name}
          </span>
          <span className="block text-xs text-t3 truncate">{[l.area, l.address].filter(Boolean).join(' · ')}</span>
        </>
      ),
      stackRender: l => l.name,
    },
    {
      key: 'status', header: 'Στάδιο', width: '140px',
      render: l => <StatusChip tone={statusTone(l.status)}>{statusLabel(l.status)}</StatusChip>,
      stackRender: l => statusLabel(l.status),
    },
    {
      key: 'next', header: 'Επόμενο βήμα', width: 'minmax(110px, 1fr)', hideBelow: 'narrow',
      render: l => (ACTIVE_STATUSES.includes(l.status) && l.next_action_at
        ? <span className={l.next_action_at < today ? 'text-red-700 dark:text-red-400 font-medium' : 'text-t2'}>{relativeDay(l.next_action_at, today)}</span>
        : <span className="text-t4">—</span>),
    },
  ], [today]);

  return (
    <>
      <ListToolbar
        filters={(
          <>
            <FilterSelect label="Στάδιο" value={statusFilter} onChange={setStatusFilter}
              options={[{ value: 'active', label: 'Ενεργοί' }, { value: 'all', label: 'Όλοι' }, ...STATUS_OPTIONS]} />
            <FilterSelect label="Είδος" value={kindFilter} onChange={setKindFilter} options={KIND_OPTIONS} allLabel="Όλα τα είδη" />
          </>
        )}
        actions={(
          <button type="button" className={GHOST_BTN} onClick={showAll} disabled={!ready || !onMap.length} data-testid="sales-map-fit">
            <LocateFixed className="w-4 h-4" aria-hidden="true" /> Δείξε όλους
          </button>
        )}
        summary={(
          <Stats testId="sales-map-counts">
            <Stat label="Στον χάρτη" value={onMap.length} />
            {approx > 0 && <Stat label="κατά προσέγγιση" value={approx} />}
            {missing > 0 && <Stat label="χωρίς θέση" value={missing} />}
            {geocoding && (
              <span className="inline-flex items-center gap-1.5 text-t3">
                <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Εντοπισμός θέσεων {geocoding.done}/{geocoding.total}
              </span>
            )}
          </Stats>
        )}
      />
      {mapError && <Notice tone="danger" testId="sales-map-error">{mapError}</Notice>}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div ref={mapEl} className="w-full h-[60vh] min-h-[360px] rounded-xl border border-line overflow-hidden bg-surface-2" data-testid="sales-map" />
        <section aria-labelledby="sales-map-visible" className="space-y-2 min-w-0">
          <h2 id="sales-map-visible" className="text-sm font-semibold text-t1">
            Σε αυτό το κομμάτι του χάρτη <span className="text-t3 font-normal">· {visible.length}</span>
          </h2>
          <DataTable columns={columns} rows={visible} rowKey={l => l.id} onRowClick={l => onOpen(l.id)} dense
            empty={<p className="px-4 py-6 text-sm text-t3">Κανένας υποψήφιος εδώ — μετακίνησε ή μίκρυνε τον χάρτη.</p>}
            rowLabel={l => l.name} testId="sales-map-list" />
        </section>
      </div>
    </>
  );
};

export default SalesMap;
